import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard,
  Car,
  MessageSquare,
  HandCoins,
  Wrench,
  Phone,
  Mail,
  Calendar,
  User,
  FileText,
  Loader2,
  LogOut,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Check,
  X,
  AlertTriangle,
  Save,
  Plus,
  Archive,
  ArchiveRestore,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feature flag for showing Google Sheets sync panel (default: hidden)
const SHOW_SHEETS_SYNC_PANEL = process.env.REACT_APP_SHOW_SHEETS_SYNC_PANEL === 'true';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('dashboard_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Format relative time helper
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// Format time helper
const formatTime = (dateStr) => {
  if (!dateStr) return '--:--';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Format date helper
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Sync Status Panel Component (only shown when enabled)
const SyncStatusPanel = ({ syncStatus, onTriggerSync, isSyncing }) => {
  if (!syncStatus) return null;
  
  const isSuccess = syncStatus.last_result === 'success';
  const isRunning = syncStatus.last_result === 'running' || isSyncing;
  const neverRun = syncStatus.last_result === 'never_run';
  const hasFailed = syncStatus.last_result === 'failed';
  
  const hasErrors = (syncStatus.skipped_errors?.length > 0) || (syncStatus.db_errors?.length > 0);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#16202A]/50 border border-white/10 p-4 md:p-6"
      data-testid="sync-status-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center bg-[#48CCCD]/20">
            <RefreshCw size={16} className={`text-[#48CCCD] md:w-5 md:h-5 ${isRunning ? 'animate-spin' : ''}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-serif text-base md:text-lg font-bold text-[#F8FAFC]">Inventory Sync</h3>
            <p className="text-[10px] md:text-xs text-[#94A3B8] font-sans">Google Sheets → Database</p>
          </div>
        </div>
        
        <Button
          onClick={onTriggerSync}
          disabled={isRunning}
          size="sm"
          className="h-8 md:h-9 px-3 md:px-4 bg-[#48CCCD] text-[#020405] hover:bg-[#3db8b8] font-sans text-[10px] md:text-xs uppercase tracking-wider disabled:opacity-50 w-full sm:w-auto"
          data-testid="trigger-sync-btn"
        >
          {isRunning ? (
            <>
              <Loader2 size={14} className="mr-1.5 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw size={14} className="mr-1.5" />
              Sync Now
            </>
          )}
        </Button>
      </div>
      
      {/* Status Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4">
        <div className="bg-[#0A0F14] p-2 md:p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Status</p>
          <div className="flex items-center gap-1.5">
            {isRunning ? (
              <RefreshCw size={14} className="text-[#48CCCD] animate-spin" />
            ) : isSuccess ? (
              <CheckCircle2 size={14} className="text-green-400" />
            ) : neverRun ? (
              <Clock size={14} className="text-[#94A3B8]" />
            ) : (
              <XCircle size={14} className="text-red-400" />
            )}
            <span className={`text-xs md:text-sm font-sans ${isSuccess ? 'text-green-400' : isRunning ? 'text-[#48CCCD]' : neverRun ? 'text-[#94A3B8]' : 'text-red-400'}`}>
              {isRunning ? 'Running' : isSuccess ? 'OK' : neverRun ? 'Pending' : 'Failed'}
            </span>
          </div>
        </div>
        
        <div className="bg-[#0A0F14] p-2 md:p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Last Run</p>
          <p className="text-xs md:text-sm text-[#F8FAFC] font-sans truncate">
            {syncStatus.last_success_at ? formatRelativeTime(syncStatus.last_success_at) : 'Never'}
          </p>
        </div>
        
        <div className="bg-[#0A0F14] p-2 md:p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Next</p>
          <p className="text-xs md:text-sm text-[#F8FAFC] font-sans">
            {syncStatus.next_run_at ? formatTime(syncStatus.next_run_at) : '--:--'}
          </p>
        </div>
        
        <div className="bg-[#0A0F14] p-2 md:p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Results</p>
          <p className="text-xs md:text-sm text-[#F8FAFC] font-sans">
            <span className="text-green-400">{syncStatus.created_count}</span>+{' '}
            <span className="text-blue-400">{syncStatus.updated_count}</span>↻{' '}
            <span className="text-yellow-400">{syncStatus.skipped_count}</span>⊘
          </p>
        </div>
      </div>
      
      {/* Error Details */}
      {(hasFailed || hasErrors) && (
        <div className="bg-red-500/10 border border-red-500/30 p-3 mt-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-red-400" />
            <span className="text-xs uppercase tracking-wider text-red-400 font-sans">
              {hasFailed ? 'Sync Failed' : 'Issues Found'}
            </span>
          </div>
          
          {syncStatus.last_error && (
            <p className="text-xs text-red-300 font-mono mb-2">{syncStatus.last_error}</p>
          )}
          
          {syncStatus.skipped_errors?.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Skipped Rows:</p>
              <ul className="text-xs text-[#94A3B8] font-mono space-y-0.5">
                {syncStatus.skipped_errors.slice(0, 5).map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
                {syncStatus.skipped_errors.length > 5 && (
                  <li className="text-[#64748B]">...and {syncStatus.skipped_errors.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#16202A]/50 border border-white/10 p-4 md:p-6"
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-3 md:gap-4">
      <div className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center ${color}`}>
        <Icon size={20} className="text-current md:w-6 md:h-6" />
      </div>
      <div>
        <p className="text-2xl md:text-3xl font-serif font-bold text-[#F8FAFC]">{value}</p>
        <p className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-sans mt-1">{label}</p>
      </div>
    </div>
  </motion.div>
);

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, leadType, isDeleting }) => {
  const [confirmText, setConfirmText] = useState('');
  const isValid = confirmText === 'DELETE';
  
  const handleClose = () => {
    setConfirmText('');
    onClose();
  };
  
  const handleConfirm = () => {
    if (isValid) {
      onConfirm();
      setConfirmText('');
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#16202A] border-white/10 text-[#F8FAFC] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-400 flex items-center gap-2">
            <Trash2 size={20} />
            Permanently Delete Lead
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            This action cannot be undone. The lead will be permanently removed from the database.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-[#F8FAFC] mb-3">
            Type <span className="font-mono bg-red-500/20 px-2 py-0.5 text-red-400">DELETE</span> to confirm:
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] font-mono"
            data-testid="delete-confirm-input"
          />
        </div>
        
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="bg-transparent border-white/20 text-[#94A3B8]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="delete-confirm-btn"
          >
            {isDeleting ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={14} className="mr-2" />
                Delete Forever
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Lead Management Controls Component
const LeadManagementControls = ({ lead, leadType, onUpdate, onArchive, onUnarchive, onDelete, isUpdating, showArchived }) => {
  const [notes, setNotes] = useState(lead.internal_notes || '');
  const [hasChanges, setHasChanges] = useState(false);
  
  const handleNotesChange = (value) => {
    setNotes(value);
    setHasChanges(value !== (lead.internal_notes || ''));
  };
  
  const handleSaveNotes = () => {
    onUpdate(leadType, lead.id, { internal_notes: notes });
    setHasChanges(false);
  };
  
  const handleToggleContacted = () => {
    onUpdate(leadType, lead.id, { contacted: !lead.contacted });
  };
  
  return (
    <div className="border-t border-white/10 mt-3 pt-3">
      {/* Action Buttons Row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {/* Contacted Toggle */}
        <button
          onClick={handleToggleContacted}
          disabled={isUpdating}
          className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-sans uppercase tracking-wider transition-colors ${
            lead.contacted 
              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
              : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
          }`}
          data-testid={`contacted-toggle-${lead.id}`}
        >
          {lead.contacted ? <Check size={12} /> : <Clock size={12} />}
          {lead.contacted ? 'Done' : 'Pending'}
        </button>
        
        {/* Archive / Unarchive */}
        {showArchived ? (
          <button
            onClick={() => onUnarchive(lead)}
            disabled={isUpdating}
            className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-sans uppercase tracking-wider bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 disabled:opacity-50"
            data-testid={`unarchive-btn-${lead.id}`}
          >
            <ArchiveRestore size={12} />
            Restore
          </button>
        ) : (
          <button
            onClick={() => onArchive(lead)}
            disabled={isUpdating}
            className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-sans uppercase tracking-wider bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 disabled:opacity-50"
            data-testid={`archive-btn-${lead.id}`}
          >
            <Archive size={12} />
            Archive
          </button>
        )}
        
        {/* Delete (only on archived) */}
        {showArchived && (
          <button
            onClick={() => onDelete(lead)}
            disabled={isUpdating}
            className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-sans uppercase tracking-wider bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50"
            data-testid={`delete-btn-${lead.id}`}
          >
            <Trash2 size={12} />
            Delete
          </button>
        )}
        
        {lead.updated_at && (
          <span className="text-[10px] text-[#64748B] font-sans ml-auto">
            Updated: {formatRelativeTime(lead.updated_at)}
          </span>
        )}
      </div>
      
      {/* Internal Notes */}
      <div className="relative">
        <textarea
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Add internal notes..."
          className="w-full px-3 py-2 bg-[#0A0F14] border border-white/10 text-[#F8FAFC] font-sans text-sm placeholder:text-[#64748B] focus:outline-none focus:border-[#48CCCD]/50 resize-none"
          rows={2}
          data-testid={`notes-input-${lead.id}`}
        />
        {hasChanges && (
          <button
            onClick={handleSaveNotes}
            disabled={isUpdating}
            className="absolute right-2 bottom-2 p-1.5 bg-[#48CCCD] text-[#020405] hover:bg-[#3db8b8] disabled:opacity-50"
            data-testid={`save-notes-${lead.id}`}
          >
            <Save size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

// Unified Lead Card Component
const LeadCard = ({ lead, leadType, onUpdate, onArchive, onUnarchive, onDelete, isUpdating, showArchived }) => {
  const renderLeadContent = () => {
    switch (leadType) {
      case 'enquiry':
        return (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
                  lead.type === 'finance' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {lead.type}
                </span>
                <h4 className="font-serif text-[#F8FAFC] font-bold mt-2">{lead.name}</h4>
              </div>
              <p className="text-[10px] text-[#94A3B8] font-sans">{formatDate(lead.created_at)}</p>
            </div>
            
            {lead.car_display_name && (
              <p className="text-sm text-[#48CCCD] font-sans mb-2 flex items-center gap-2">
                <Car size={14} /> {lead.car_display_name}
              </p>
            )}
            
            <div className="flex flex-wrap gap-3 text-xs text-[#94A3B8] font-sans mb-2">
              <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[#48CCCD] transition-colors">
                <Mail size={12} /> {lead.email}
              </a>
              {lead.phone && (
                <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#48CCCD] transition-colors">
                  <Phone size={12} /> {lead.phone}
                </a>
              )}
            </div>
            
            {lead.type === 'finance' && lead.monthly_payment_estimate && (
              <div className="bg-[#16202A] p-2 mt-2 text-xs font-sans">
                <p className="text-[#F8FAFC]">
                  Deposit: €{lead.deposit?.toLocaleString()} | {lead.term_months}mo | 
                  <span className="text-[#48CCCD] font-bold ml-1">€{lead.monthly_payment_estimate?.toFixed(0)}/mo</span>
                </p>
              </div>
            )}
            
            {lead.message && (
              <p className="text-sm text-[#94A3B8] font-sans mt-2 italic line-clamp-2">&ldquo;{lead.message}&rdquo;</p>
            )}
          </>
        );
        
      case 'sell_car':
        return (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
                  lead.condition_rating === 'excellent' ? 'bg-green-500/20 text-green-400' :
                  lead.condition_rating === 'good' ? 'bg-blue-500/20 text-blue-400' :
                  lead.condition_rating === 'average' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {lead.condition_rating}
                </span>
                <h4 className="font-serif text-[#F8FAFC] font-bold mt-2">
                  {lead.year} {lead.make} {lead.model}
                </h4>
              </div>
              <p className="text-[10px] text-[#94A3B8] font-sans">{formatDate(lead.created_at)}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-xs font-sans mb-2">
              <p className="text-[#94A3B8]">Reg: <span className="text-[#F8FAFC] font-mono">{lead.reg_number}</span></p>
              <p className="text-[#94A3B8]">Mileage: <span className="text-[#F8FAFC]">{lead.mileage_km?.toLocaleString()} km</span></p>
            </div>
            
            <div className="border-t border-white/10 pt-2 mb-2">
              <p className="text-sm text-[#F8FAFC] font-sans font-medium">{lead.owner_name}</p>
              <div className="flex flex-wrap gap-3 text-xs text-[#94A3B8] font-sans mt-1">
                <a href={`mailto:${lead.owner_email}`} className="flex items-center gap-1 hover:text-[#48CCCD]">
                  <Mail size={12} /> {lead.owner_email}
                </a>
                <a href={`tel:${lead.owner_phone}`} className="flex items-center gap-1 hover:text-[#48CCCD]">
                  <Phone size={12} /> {lead.owner_phone}
                </a>
              </div>
            </div>
          </>
        );
        
      case 'repair':
        return (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-serif text-[#F8FAFC] font-bold">{lead.name}</h4>
                <p className="text-sm text-[#48CCCD] font-sans mt-1">
                  {lead.year ? `${lead.year} ` : ''}{lead.make} {lead.model} ({lead.car_reg})
                </p>
              </div>
              <p className="text-[10px] text-[#94A3B8] font-sans">{formatDate(lead.created_at)}</p>
            </div>
            
            <div className="flex flex-wrap gap-3 text-xs text-[#94A3B8] font-sans mb-2">
              <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[#48CCCD]">
                <Mail size={12} /> {lead.email}
              </a>
              <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#48CCCD]">
                <Phone size={12} /> {lead.phone}
              </a>
            </div>
            
            {lead.issue_summary && (
              <div className="bg-[#16202A] p-2 mt-2 text-xs font-sans">
                <p className="text-[#F8FAFC]">{lead.issue_summary}</p>
              </div>
            )}
          </>
        );
        
      case 'contact':
        return (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-serif text-[#F8FAFC] font-bold">{lead.name}</h4>
                {lead.subject && (
                  <p className="text-sm text-[#48CCCD] font-sans mt-1">{lead.subject}</p>
                )}
              </div>
              <p className="text-[10px] text-[#94A3B8] font-sans">{formatDate(lead.created_at)}</p>
            </div>
            
            <div className="flex flex-wrap gap-3 text-xs text-[#94A3B8] font-sans mb-2">
              <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[#48CCCD]">
                <Mail size={12} /> {lead.email}
              </a>
              {lead.phone && (
                <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#48CCCD]">
                  <Phone size={12} /> {lead.phone}
                </a>
              )}
            </div>
            
            <p className="text-sm text-[#94A3B8] font-sans italic line-clamp-2">&ldquo;{lead.message}&rdquo;</p>
          </>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div 
      className={`bg-[#0A0F14] border p-3 md:p-4 mb-3 last:mb-0 ${
        lead.archived ? 'border-gray-500/30 opacity-75' :
        lead.contacted ? 'border-green-500/30' : 'border-yellow-500/30'
      }`}
      data-testid={`lead-card-${lead.id}`}
    >
      {lead.archived && (
        <div className="flex items-center gap-1 mb-2 text-[10px] text-gray-400 uppercase tracking-wider">
          <Archive size={12} />
          Archived {lead.archived_at && formatRelativeTime(lead.archived_at)}
        </div>
      )}
      {renderLeadContent()}
      <LeadManagementControls
        lead={lead}
        leadType={leadType}
        onUpdate={onUpdate}
        onArchive={onArchive}
        onUnarchive={onUnarchive}
        onDelete={onDelete}
        isUpdating={isUpdating}
        showArchived={showArchived}
      />
    </div>
  );
};

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState(null);
  const [updatingLeads, setUpdatingLeads] = useState(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Filter state
  const [showArchived, setShowArchived] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    leadType: 'all',
    contacted: 'all',
    dateRange: 'all'
  });
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, lead: null, leadType: null });
  const [isDeleting, setIsDeleting] = useState(false);
  
  const navigate = useNavigate();

  // Logout handler
  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('dashboard_token');
    navigate('/dashboard/login');
  }, [navigate]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    const headers = getAuthHeaders();
    try {
      const response = await fetch(`${API}/dashboard/summary`, { headers });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [handleLogout]);

  // Fetch leads with pagination
  const fetchLeads = useCallback(async (resetPage = false) => {
    const token = sessionStorage.getItem('dashboard_token');
    if (!token) {
      navigate('/dashboard/login');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const headers = getAuthHeaders();
    const currentPage = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    
    try {
      const leadTypes = filters.leadType === 'all' 
        ? ['enquiry', 'sell_car', 'repair', 'contact']
        : [filters.leadType];
      
      let allLeads = [];
      let sumTotal = 0;
      
      for (const leadType of leadTypes) {
        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        params.append('page_size', pageSize.toString());
        
        if (filters.search) params.append('q', filters.search);
        if (filters.contacted !== 'all') {
          params.append('contacted', filters.contacted === 'contacted' ? 'true' : 'false');
        }
        if (filters.dateRange !== 'all') params.append('date_range', filters.dateRange);
        
        if (showArchived) {
          params.append('archived_only', 'true');
        }
        
        const response = await fetch(`${API}/dashboard/leads/${leadType}?${params}`, { headers });
        
        if (response.status === 401) {
          handleLogout();
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          const leadsWithType = data.items.map(item => ({ ...item, _leadType: leadType }));
          allLeads = [...allLeads, ...leadsWithType];
          sumTotal += data.total_count;
        }
      }
      
      // Sort: not contacted first, then by date
      allLeads.sort((a, b) => {
        if (a.contacted !== b.contacted) return a.contacted ? 1 : -1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setLeads(allLeads);
      setTotalCount(sumTotal);
      setTotalPages(Math.ceil(sumTotal / pageSize));
      setHasMore(currentPage < Math.ceil(sumTotal / pageSize));
      
      // Fetch sync status if panel is shown
      if (SHOW_SHEETS_SYNC_PANEL) {
        const syncRes = await fetch(`${API}/sync/status`);
        if (syncRes.ok) {
          setSyncStatus(await syncRes.json());
        }
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setError('Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate, handleLogout, page, pageSize, filters, showArchived]);

  useEffect(() => {
    fetchSummary();
    fetchLeads();
  }, []);

  // Refetch when filters or pagination change
  useEffect(() => {
    fetchLeads();
  }, [page, pageSize, showArchived]);

  // Handle filter changes (reset to page 1)
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Apply filters (triggers fetch with page reset)
  const applyFilters = useCallback(() => {
    fetchLeads(true);
  }, [fetchLeads]);

  // Update lead handler
  const handleUpdateLead = useCallback(async (leadType, leadId, updates) => {
    const key = `${leadType}-${leadId}`;
    setUpdatingLeads(prev => new Set([...prev, key]));
    
    try {
      const response = await fetch(`${API}/dashboard/leads/${leadType}/${leadId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updates)
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (response.ok) {
        setLeads(prev => prev.map(item => 
          item.id === leadId && item._leadType === leadType
            ? { ...item, ...updates, updated_at: new Date().toISOString() }
            : item
        ));
      }
    } catch (err) {
      console.error('Failed to update lead:', err);
    } finally {
      setUpdatingLeads(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [handleLogout]);

  // Archive lead handler
  const handleArchiveLead = useCallback(async (lead) => {
    const key = `${lead._leadType}-${lead.id}`;
    setUpdatingLeads(prev => new Set([...prev, key]));
    
    try {
      const response = await fetch(`${API}/dashboard/leads/${lead._leadType}/${lead.id}/archive`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (response.ok) {
        // Remove from list
        setLeads(prev => prev.filter(item => !(item.id === lead.id && item._leadType === lead._leadType)));
        fetchSummary();
      }
    } catch (err) {
      console.error('Failed to archive lead:', err);
    } finally {
      setUpdatingLeads(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [handleLogout, fetchSummary]);

  // Unarchive lead handler
  const handleUnarchiveLead = useCallback(async (lead) => {
    const key = `${lead._leadType}-${lead.id}`;
    setUpdatingLeads(prev => new Set([...prev, key]));
    
    try {
      const response = await fetch(`${API}/dashboard/leads/${lead._leadType}/${lead.id}/unarchive`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (response.ok) {
        // Remove from archived list
        setLeads(prev => prev.filter(item => !(item.id === lead.id && item._leadType === lead._leadType)));
        fetchSummary();
      }
    } catch (err) {
      console.error('Failed to unarchive lead:', err);
    } finally {
      setUpdatingLeads(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [handleLogout, fetchSummary]);

  // Delete lead handler
  const handleDeleteLead = useCallback((lead) => {
    setDeleteModal({ open: true, lead, leadType: lead._leadType });
  }, []);

  const confirmDeleteLead = useCallback(async () => {
    if (!deleteModal.lead) return;
    
    setIsDeleting(true);
    
    try {
      const response = await fetch(`${API}/dashboard/leads/${deleteModal.leadType}/${deleteModal.lead.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ confirm: 'DELETE' })
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (response.ok) {
        // Remove from list
        setLeads(prev => prev.filter(item => 
          !(item.id === deleteModal.lead.id && item._leadType === deleteModal.leadType)
        ));
        setDeleteModal({ open: false, lead: null, leadType: null });
      }
    } catch (err) {
      console.error('Failed to delete lead:', err);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal, handleLogout]);

  // Trigger sync handler
  const handleTriggerSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await fetch(`${API}/sync/trigger`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data.status);
      }
    } catch (err) {
      console.error('Failed to trigger sync:', err);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // CSV Export handler - uses fetch with auth header and blob download
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExportCSV = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.search) params.append('q', filters.search);
    if (filters.contacted !== 'all') {
      params.append('contacted', filters.contacted === 'contacted' ? 'true' : 'false');
    }
    if (filters.dateRange !== 'all') params.append('date_range', filters.dateRange);
    if (showArchived) params.append('archived_only', 'true');
    
    const leadType = filters.leadType === 'all' ? '' : `${filters.leadType}/`;
    const url = `${API}/dashboard/leads/${leadType}export.csv?${params}`;
    
    setIsExporting(true);
    
    try {
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const typeLabel = filters.leadType === 'all' ? 'all' : filters.leadType;
      link.download = `cmac_${typeLabel}_leads_${date}.csv`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('CSV export failed:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [filters, showArchived, handleLogout]);

  // Get lead counts for filter dropdown
  const leadCounts = useMemo(() => {
    if (!summary) return { all: 0, enquiry: 0, sell_car: 0, repair: 0, contact: 0 };
    return {
      all: summary.total_enquiries + summary.total_sell_car_leads + summary.total_repair_enquiries + summary.total_contact_enquiries,
      enquiry: summary.total_enquiries,
      sell_car: summary.total_sell_car_leads,
      repair: summary.total_repair_enquiries,
      contact: summary.total_contact_enquiries
    };
  }, [summary]);

  const notContactedCount = useMemo(() => {
    return leads.filter(l => !l.contacted && !l.archived).length;
  }, [leads]);

  if (loading && leads.length === 0) {
    return (
      <div className="min-h-screen bg-[#020405] pt-20 flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center">
          <Loader2 size={48} className="text-[#48CCCD] animate-spin mx-auto mb-4" />
          <p className="text-[#94A3B8] font-sans">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#020405] pt-20 flex items-center justify-center" data-testid="dashboard-error">
        <div className="text-center">
          <p className="text-red-400 font-sans mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020405] pt-20 overflow-x-hidden" data-testid="dashboard-page">
      {/* Header */}
      <section className="py-8 md:py-16 bg-gradient-to-br from-[#16202A] via-[#0A0F14] to-[#020405]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center bg-[#48CCCD]/20">
                <LayoutDashboard size={20} className="text-[#48CCCD] md:w-6 md:h-6" />
              </div>
              <div className="min-w-0">
                <h1 
                  className="font-serif text-2xl md:text-4xl font-bold text-[#F8FAFC] tracking-tight truncate"
                  data-testid="dashboard-title"
                >
                  Dealer Dashboard
                </h1>
                <p className="text-[#94A3B8] font-sans text-xs md:text-sm">
                  {notContactedCount > 0 && !showArchived && (
                    <span className="text-yellow-400">
                      {notContactedCount} leads need follow-up
                    </span>
                  )}
                  {showArchived && (
                    <span className="text-gray-400">Viewing archived leads</span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <Button
                onClick={() => navigate('/dashboard/cars')}
                variant="outline"
                size="sm"
                className="h-9 px-3 bg-transparent border border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40 font-sans text-[10px] md:text-xs uppercase tracking-wider"
                data-testid="manage-cars-btn"
              >
                <Car size={14} className="mr-1.5" />
                <span className="hidden sm:inline">Manage</span> Cars
              </Button>
              <Button
                onClick={() => navigate('/dashboard/cars/new')}
                size="sm"
                className="h-9 px-3 bg-[#48CCCD] text-[#020405] hover:bg-[#3db8b8] font-sans text-[10px] md:text-xs uppercase tracking-wider"
                data-testid="add-car-btn"
              >
                <Plus size={14} className="mr-1.5" />
                Add Car
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="h-9 px-3 bg-transparent border border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40 font-sans text-[10px] md:text-xs uppercase tracking-wider"
                data-testid="dashboard-logout-btn"
              >
                <LogOut size={14} className="mr-1.5" />
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Exit</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="py-6 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            <StatCard
              icon={Car}
              label="Cars"
              value={summary?.total_cars || 0}
              color="bg-[#48CCCD]/20 text-[#48CCCD]"
            />
            <StatCard
              icon={MessageSquare}
              label="Enquiries"
              value={summary?.total_enquiries || 0}
              color="bg-blue-500/20 text-blue-400"
            />
            <StatCard
              icon={HandCoins}
              label="Sell Leads"
              value={summary?.total_sell_car_leads || 0}
              color="bg-green-500/20 text-green-400"
            />
            <StatCard
              icon={Calendar}
              label="Appointments"
              value={summary?.total_repair_enquiries || 0}
              color="bg-orange-500/20 text-orange-400"
            />
            <StatCard
              icon={FileText}
              label="Contact"
              value={summary?.total_contact_enquiries || 0}
              color="bg-purple-500/20 text-purple-400"
            />
          </div>
        </div>
      </section>

      {/* Sync Status Panel - Only shown when enabled */}
      {SHOW_SHEETS_SYNC_PANEL && (
        <section className="py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <SyncStatusPanel 
              syncStatus={syncStatus} 
              onTriggerSync={handleTriggerSync}
              isSyncing={isSyncing}
            />
          </div>
        </section>
      )}

      {/* Leads Section */}
      <section className="py-6 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {/* Tabs: Active / Archived */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => { setShowArchived(false); setPage(1); }}
              className={`px-4 py-2 text-sm font-sans uppercase tracking-wider transition-colors ${
                !showArchived 
                  ? 'bg-[#48CCCD] text-[#020405]' 
                  : 'bg-[#16202A] text-[#94A3B8] hover:text-white'
              }`}
              data-testid="active-tab"
            >
              Active
            </button>
            <button
              onClick={() => { setShowArchived(true); setPage(1); }}
              className={`px-4 py-2 text-sm font-sans uppercase tracking-wider transition-colors ${
                showArchived 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-[#16202A] text-[#94A3B8] hover:text-white'
              }`}
              data-testid="archived-tab"
            >
              <Archive size={14} className="inline mr-1.5" />
              Archived
            </button>
          </div>
          
          {/* Filter Controls */}
          <div className="bg-[#16202A]/50 border border-white/10 p-3 md:p-4 mb-4 md:mb-6" data-testid="filter-controls">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
              {/* Search */}
              <div className="w-full sm:flex-1 sm:min-w-[180px] relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  type="text"
                  placeholder="Search name, email, phone..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                  className="w-full pl-10 pr-3 py-2 bg-[#0A0F14] border border-white/10 text-[#F8FAFC] font-sans text-sm placeholder:text-[#64748B] focus:outline-none focus:border-[#48CCCD]/50"
                  data-testid="search-input"
                />
              </div>
              
              {/* Filters row */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Lead Type Filter */}
                <select
                  value={filters.leadType}
                  onChange={(e) => handleFilterChange('leadType', e.target.value)}
                  className="flex-1 min-w-[100px] px-2 py-2 bg-[#0A0F14] border border-white/10 text-[#F8FAFC] font-sans text-xs sm:text-sm focus:outline-none focus:border-[#48CCCD]/50"
                  data-testid="lead-type-filter"
                >
                  <option value="all">All ({leadCounts.all})</option>
                  <option value="enquiry">Enquiries ({leadCounts.enquiry})</option>
                  <option value="sell_car">Sell ({leadCounts.sell_car})</option>
                  <option value="repair">Appointments ({leadCounts.repair})</option>
                  <option value="contact">Contact ({leadCounts.contact})</option>
                </select>
                
                {/* Contacted Filter */}
                <select
                  value={filters.contacted}
                  onChange={(e) => handleFilterChange('contacted', e.target.value)}
                  className="flex-1 min-w-[90px] px-2 py-2 bg-[#0A0F14] border border-white/10 text-[#F8FAFC] font-sans text-xs sm:text-sm focus:outline-none focus:border-[#48CCCD]/50"
                  data-testid="contacted-filter"
                >
                  <option value="all">All Status</option>
                  <option value="not_contacted">Pending</option>
                  <option value="contacted">Done</option>
                </select>
                
                {/* Date Range Filter */}
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className="flex-1 min-w-[80px] px-2 py-2 bg-[#0A0F14] border border-white/10 text-[#F8FAFC] font-sans text-xs sm:text-sm focus:outline-none focus:border-[#48CCCD]/50"
                  data-testid="date-range-filter"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="7">7 Days</option>
                  <option value="30">30 Days</option>
                </select>
                
                <Button
                  onClick={applyFilters}
                  size="sm"
                  className="h-9 px-3 bg-[#48CCCD] text-[#020405]"
                >
                  <Search size={14} />
                </Button>
                
                {/* Export CSV */}
                <Button
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 bg-transparent border border-white/20 text-[#94A3B8] hover:text-white disabled:opacity-50"
                  data-testid="export-csv-btn"
                >
                  {isExporting ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Download size={14} className="mr-1.5" />
                  )}
                  <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'CSV'}</span>
                </Button>
              </div>
            </div>
          </div>
          
          {/* Results Summary & Page Size */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <p className="text-sm text-[#94A3B8] font-sans">
              Showing <span className="text-[#F8FAFC] font-medium">{leads.length}</span> of{' '}
              <span className="text-[#F8FAFC] font-medium">{totalCount}</span> leads
              {showArchived && ' (archived)'}
            </p>
            
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#64748B]">Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
                className="px-2 py-1 bg-[#0A0F14] border border-white/10 text-[#F8FAFC] font-sans text-xs"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
          
          {/* Leads List */}
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="text-[#48CCCD] animate-spin" />
              </div>
            ) : leads.length === 0 ? (
              <div className="bg-[#16202A]/50 border border-white/10 p-12 text-center">
                <p className="text-[#94A3B8] font-sans">
                  {showArchived ? 'No archived leads' : 'No leads match your filters'}
                </p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto space-y-3 pr-2">
                {leads.map(lead => (
                  <LeadCard
                    key={`${lead._leadType}-${lead.id}`}
                    lead={lead}
                    leadType={lead._leadType}
                    onUpdate={handleUpdateLead}
                    onArchive={handleArchiveLead}
                    onUnarchive={handleUnarchiveLead}
                    onDelete={handleDeleteLead}
                    isUpdating={updatingLeads.has(`${lead._leadType}-${lead.id}`)}
                    showArchived={showArchived}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                variant="outline"
                size="sm"
                className="h-9 px-3 bg-transparent border-white/20 text-[#94A3B8] disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </Button>
              
              <span className="text-sm text-[#94A3B8] font-sans px-4">
                Page {page} of {totalPages}
              </span>
              
              <Button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                variant="outline"
                size="sm"
                className="h-9 px-3 bg-transparent border-white/20 text-[#94A3B8] disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </section>
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, lead: null, leadType: null })}
        onConfirm={confirmDeleteLead}
        leadType={deleteModal.leadType}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default DashboardPage;
